import React, { useMemo } from 'react';

// DEPENDENCIES
import isUndefined from 'lodash/isUndefined';
import { format, toZonedTime } from 'date-fns-tz';
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { NEW_USED } from 'codes';
import { DATE_FORMATS } from 'globals.js';

// GLOBAL FUNCTIONS
import { formatDollar } from 'functions.js';

// GLOBAL COMPONENTS
import Card from 'parts/card/Card';
import Form from 'core/form/Form';
import Sprite from 'core/tools/Sprite';

// LOCAL COMPONENTS
import DmsTooltip from '../../../tools/DmsTooltip';
import Attachment from 'routers/interface/tools/attachment/Attachment';

// STYLES
import styles from './pricingInfo.module.scss';

// MAIN COMPONENT
const PricingInfoForm = () => {
  const { values: { inventory = {} } = {} } = useFormikContext() || {};

  const {
    date_dms_sync,
    dms_invoice_price,
    dmsinvoice,
    new_used,
    retail_price,
    internet_price,
    invoice_cost,
    documentAttachment = {}
  } = inventory;
  const isNew = useMemo(() => new_used === NEW_USED.new.numeric, [new_used]);
  const type = useMemo(() => (!isUndefined(new_used) ? (isNew ? 'Invoice Cost' : 'ACV') : ''), [new_used, isNew]);

  // If now, convert to EST
  const easternTimeZone = 'America/New_York';
  const lastDmsSync = useMemo(() => {
    const date = date_dms_sync ? new Date(date_dms_sync) : toZonedTime(new Date(), easternTimeZone);
    const formatted = format(date, DATE_FORMATS.dateTime, { timeZone: easternTimeZone });
    return !date_dms_sync ? (
      <div className="d-flex align-items-center">
        <Sprite className="me-1 text-danger" use="cancel" /> {formatted}
      </div>
    ) : (
      formatted
    );
  }, [date_dms_sync]);

  const retailPriceLabel = useMemo(() => (isNew ? 'MSRP' : 'Retail'), [isNew]);
  const invoiceAttachmentLabel = useMemo(() => (isNew ? 'Factory Invoice' : 'Purchase Invoice'), [isNew]);

  // RENDER
  return (
    <Form.Body>
      <Form.PlainText label={retailPriceLabel} value={retail_price || 0} schema="dollars" />
      {isNew && <Attachment.Link label="MSRP Sticker/Window Sticker" attachments={documentAttachment.msrp} />}
      <Form.PlainText label="Internet" value={internet_price || 0} schema="dollars" />
      <Form.PlainText label={type} value={invoice_cost || 0} schema="dollars" />
      <Attachment.Link label={invoiceAttachmentLabel} attachments={documentAttachment.invoice} />
      <Form.PlainText
        label="DMS Cost"
        value={`${dms_invoice_price ? formatDollar(dms_invoice_price) : 'Not available'} ${dmsinvoice}`}
        suffix={<DmsTooltip />}
        fullWidth
        className={styles.control}
      />
      <Form.PlainText
        label="Last DMS Sync"
        value={lastDmsSync}
        suffix={<DmsTooltip />}
        fullWidth
        className={styles.control}
      />
    </Form.Body>
  );
};

export default function PricingInfo(props) {
  return <Card title="Pricing Info" body={<PricingInfoForm {...props} />} />;
}
